import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import React, { useMemo, useState } from 'react';
import { Content } from '../../../../apiService';
import ContentGenres from '../../../../ContentGenres';
import ContentRatings from '../../../../ContentRatings';
import CountryYear from '../../../../CountryYear';
import { Card, CardBody, CardImage, Flex, Icon, Text } from '../../../../styles';
import CardBadge from '../../../../CardBadge';
import { IconLock } from '../../../../icons';
import DesktopOnlyWrapper from '../../../../styles/DesktopOnlyWrapper';
import useDelayedHover from '../../../../useDelayedHover';
import usePauseHeroVideo from '../../../../utils/usePauseHeroVideo';
import { getWidgetsTeaserPath, inspectFlags, usePlayerService } from '../../../../utils';
import { __t } from '../../../../i18n';
import moment from 'moment-jalaali';
import LivePromotedCardVideoPreview from './LivePromotedCardVideoPreview';
import IconLiveStreaming from '../../../../icons/IconLiveStreaming';

moment.loadPersian({ dialect: 'persian-modern' });

interface PromotedCardProps {
  imagePath?: string;
  content: Content;
  conductorTitle?: string;
  startStreamTime?: string;
  isLive?: boolean;
  active?: boolean;
  onHoverChange?: (id: string | null) => void;
}

export default function LivePromotedCard({ imagePath, content, conductorTitle, startStreamTime, isLive = false, active = false, onHoverChange }: PromotedCardProps) {
  const { short_id, page_title: title, flag, id } = content;
  // Memoized so the reference stays stable across re-renders (e.g. from usePlayerService),
  // otherwise HlsPlayer would re-initialize on every render and the preview never plays.
  const teaserPath = useMemo(
    () => getWidgetsTeaserPath(content?.video_files, content.id),
    [content?.video_files, content.id],
  );

  const { playable, hasAccess, ageRestricted, isForeignIP, free, hasSpecificCarrie, blocked } = inspectFlags([content?.conditional_flag, content?.flag].join(', ') || '');
  const accessRestricted = isForeignIP;
  // Restricted channels never reveal anything on hover (no preview, no title), like the carousel card.
  const restricted = ageRestricted || accessRestricted || blocked;
  const previewable = !restricted && teaserPath?.type === 'application/x-mpegURL' && !!teaserPath?.src;

  const [handlePlay] = usePlayerService({ id: short_id, playable, hasAccess, free, hasSpecificCarrie });
  // Live & watchable -> player; needs login -> login (handled by usePlayerService). No live content -> nothing.
  const handleClick = () => {
    if (!playable || accessRestricted || ageRestricted || blocked) return;
    if (isLive && short_id) handlePlay('l');
  };

  const [hovered, setHovered] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const { pauseHero, resumeHero } = usePauseHeroVideo();

  const delayedHover = useDelayedHover((isHovered) => {
    setHovered(isHovered);
    setPreviewPlaying(isHovered && previewable);
    onHoverChange?.(previewable && isHovered ? id : null);
  });

  const onMouseEnter = () => {
    if ((typeof window !== 'undefined' && window.innerWidth <= Number(1200)) || restricted) return;
    pauseHero();
    delayedHover(true);
  };

  const onMouseLeave = () => {
    delayedHover(false);
    resumeHero();
  };

  return (
    <CardCell>
      <Card
        orientation="horizontal"
        style={{ backgroundImage: 'url(/static/images/placeholder/promoted.svg)', cursor: isLive ? 'pointer' : 'default' }}
        onClick={handleClick}
        block
      >
        <CardImage src={`${imagePath}?w=500`} alt={title} loading="lazy" />

        {ageRestricted && (
          <MessageBg>
            <>
              <IconLock />
              <Text variant="body2" my="0.5rem">
                {__t('live.age_restricted')}
              </Text>
            </>
          </MessageBg>
        )}

        {accessRestricted && (
          <MessageBg>
            <>
              <IconLock />
              <Text variant="body2" my="0.5rem">
                {__t('live.ip_restricted')}
              </Text>
            </>
          </MessageBg>
        )}

        {blocked && content?.conditional_flag_description && (
          <MessageBg>
            <>
              <IconLock />
              <Text variant="body2" my="0.5rem">
                {content?.conditional_flag_description}
              </Text>
            </>
          </MessageBg>
        )}

        {/* Standard content badges (free / coming soon / cinema, etc.), same as PromotedCard. The
          "free" flag (traffic_free) lives in conditional_flag, so both flag strings are passed. */}
        {!(hovered && previewable) && <CardBadge flag={`${flag ?? ''}, ${content?.conditional_flag ?? ''}`} />}
        {/* Playing/schedule label, exactly like LiveCarouselCard; hidden while hovering. */}
        <Badges $hidden={hovered}>
          <BadgeWrapper>
            {isLive
              ? <PlayingBadge align='center'>
                <BlinkingIcon size='large'>
                  <IconLiveStreaming/>
                </BlinkingIcon>
                <Text variant="body2" weight="bold">{__t('common.live')}</Text>
              </PlayingBadge>
              : <Schedule streamTime={startStreamTime} />}
          </BadgeWrapper>
        </Badges>
        <MobileCard>
          <CardBody>
            <DetailInfo content={content}/>
          </CardBody>
        </MobileCard>
        {!restricted && (
          <DesktopOnlyWrapper>
            <CardHoverBody
              hasTeaser={previewable}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
            >
              {hovered && (
                previewable ? (
                  previewPlaying && (
                    <LivePromotedCardVideoPreview
                      teaser={teaserPath}
                      active={active}
                      content={content}
                      title={conductorTitle}
                      onEnded={() => setPreviewPlaying(false)}
                    />
                  )
                ) : (
                // Scheduled channel: reveal only the title on hover (no video / extra details).
                  <Container>
                    <Title mb={1} variant="body1" as="h2">{conductorTitle || content.title}</Title>
                  </Container>
                )
              )}
            </CardHoverBody>
          </DesktopOnlyWrapper>
        )}
      </Card>

      {/* Title sits outside the card, so it keeps its size; it just fades out while hovering. */}
      <CardTitle $hidden={hovered && !restricted} variant="body2">
        {conductorTitle || content.title}
      </CardTitle>
    </CardCell>
  );
}

const DetailInfo = ({ content }) => {
  return <Container>
    <Title mb={1} variant="body1" as="h2">{content.title}</Title>
    <div className="desktop-only">
      <CountryYear mb={1} label {...content} />
      <ContentRatings styleSystem={{ mb: 1 }} {...content} />
    </div>
    <ContentGenres categories={content.categories} link={false} nowrap />
  </Container>;
};

// The wrapper cell — keeps the title in flow directly below the card.
const CardCell = styled.div(() => ({
  position: 'relative',
}));

// Under-card title — fades out while the card reveals its hover content.
const CardTitle = styled(Text)<{ $hidden?: boolean }>(({ theme, $hidden }) => ({
  display: 'block',
  maxWidth: '100%',
  marginTop: '0.5rem',
  marginBottom: '0.5rem',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  color: theme.palette.foreground[900][0],
  transition: 'opacity 240ms ease',
  opacity: $hidden ? 0 : 1,
}));

const MobileCard = styled.div(() => ({
  display: 'block',
  '@media (min-width: 1200px)': {
    display: 'none',
  },
}));

const CardHoverBody = styled(CardBody)<{ hasTeaser: boolean }>(({ theme, hasTeaser }) => ({
  zIndex: 1,
  // A live channel plays the video edge-to-edge (transparent, no padding); a scheduled one
  // reveals just its title over the dimmed CardBody backdrop.
  padding: hasTeaser ? 0 : theme.spacing(1),
  ...(hasTeaser && {
    backgroundColor: 'transparent',
  }),
  // The base CardBody only reveals itself via an `a:hover` selector; the card is now a
  // div (onClick, not a link), so reveal it on its own hover instead.
  '&:hover': {
    opacity: 1,
  },
}));

const Badges = styled.div<{ $hidden?: boolean }>(({ $hidden }) => ({
  transition: 'opacity 240ms ease',
  opacity: $hidden ? 0 : 1,
  pointerEvents: $hidden ? 'none' : undefined,
}));

// Age/IP restriction overlay — same as LiveCarouselCard.
const MessageBg = styled.div(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.rgba(theme.palette.background[800][0], .8),
  padding: theme.spacing(1),
}));

const BadgeWrapper = styled.div(() => ({
  position: 'absolute',
  zIndex: 1,
  pointerEvents: 'none',
  bottom: 8,
  right: 8,
}));

// Live icon pulses between white and the theme's main color. CSS `fill` overrides the SVG's
// inline `fill="#fff"`, so the underlying icon component stays untouched.
const blink = (color: string) => keyframes({
  '0%, 100%': { fill: '#fff' },
  '50%': { fill: color },
});

const BlinkingIcon = styled(Icon)(({ theme }) => ({
  '& svg g': {
    animation: `${blink(theme.palette.primary[900][0])} 1.2s ease-in-out infinite`,
  },
}));

// Glassy "playing" label — same translucent, blurred background as the player's live badge.
const PlayingBadge = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  gap: '0.25em',
  padding: '0.25em 1em',
  borderRadius: theme.radius.pill,
  background: theme.rgba(theme.palette.foreground[800][1], 0.7),
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
  color: theme.palette.text[700][0],
}));

const Schedule = ({ streamTime }: { streamTime?: string }) => {
  const date = moment(streamTime);
  const daysToLive = date.diff(moment(), 'days');
  return (
    <PlayingBadge>
      <Text variant="body2">
        {daysToLive >= 7
          ? date.format('jD jMMMM')
          : (moment().diff(date, 'days') >= 2) ? date.fromNow() : date.calendar().split(' ')[0]}
        <span style={{ marginInline: '4px', display: 'inline-block' }}>-</span>
        {moment(date).format('HH:mm')}
      </Text>
    </PlayingBadge>
  );
};

const Container = styled.div(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  '& .desktop-only': {
    display: 'none',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 2),
    '& .desktop-only': {
      display: 'block',
    },
  },
  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(2, 4),
  },
}));

const Title = styled(Text)(({ theme }) => ({
  fontWeight: theme.font.weight.demibold,
  ...theme.font.responsiveSize(1),
  [theme.breakpoints.up('md')]: {
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexShrink: 0,
  },
}));
